/**
 * CryptoCheckout — FIN-PAY-001 (HNT/Solana-Premium)
 *
 * Fordert per requestCryptoQuote eine Quote an (BE legt UpgradeCode an + liefert QR-Daten),
 * rendert den Wallet-QR-Code (Parität zu main: https://wallet.helium.com/payment?payee=…&amount=<bones>)
 * und pollt anschließend GET /api/v2/subscription. Sobald der produktive Cronjob die On-Chain-Zahlung
 * erkannt und Premium aktiviert hat, wird das Abo aktiv und onSuccess ausgelöst.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { QRCodeSVG } from 'qrcode.react'
import type { Plan } from './useSubscriptionPlans'
import type { DurationMonths } from './subscriptionUtils'
import {
  buildHeliumPaymentUrl,
  type CryptoQuote,
  type Subscription,
} from './useSubscription'

const POLL_INTERVAL_MS = 8000

type CryptoCheckoutProps = {
  plan: Plan
  duration: DurationMonths
  onSuccess: () => void
  onClose: () => void
  requestCryptoQuote: (planId: string, durationMonths: number) => Promise<CryptoQuote>
  refetchSubscription: () => Promise<{ data: Subscription | null | undefined }> | void
}

function formatCountdown(ms: number, expiredText: string): string {
  if (ms <= 0) return expiredText
  const totalMin = Math.floor(ms / 60000)
  const days = Math.floor(totalMin / 1440)
  const hours = Math.floor((totalMin % 1440) / 60)
  const min = totalMin % 60
  if (days > 0) return `${days}d ${hours}h`
  if (hours > 0) return `${hours}h ${min}min`
  return `${min}min`
}

export function CryptoCheckout({
  plan,
  duration,
  onSuccess,
  onClose,
  requestCryptoQuote,
  refetchSubscription,
}: CryptoCheckoutProps) {
  const { t } = useTranslation('subscription')
  const [quote, setQuote] = useState<CryptoQuote | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState<'address' | 'amount' | null>(null)
  const [now, setNow] = useState<number>(() => Date.now())
  // Promise cachen statt nur ein bool-Flag: unter React-StrictMode (Doppel-Mount im Dev)
  // wird die Quote so genau EINMAL angefordert, aber JEDER Mount hängt seinen eigenen
  // active-geschützten Handler an. Ein reiner requestedRef-Guard würde den zweiten (lebenden)
  // Mount blockieren, während die Antwort des ersten Mounts mit active=false verpufft → QR
  // bliebe ewig im Ladezustand hängen.
  const quotePromiseRef = useRef<Promise<CryptoQuote> | null>(null)

  // Quote einmalig anfordern.
  useEffect(() => {
    let active = true
    if (!quotePromiseRef.current) {
      quotePromiseRef.current = requestCryptoQuote(plan.id, duration)
    }
    quotePromiseRef.current
      .then((q) => { if (active) setQuote(q) })
      .catch(() => { if (active) setError(t('cryptoCheckout.quoteError')) })
    return () => { active = false }
  }, [plan.id, duration, requestCryptoQuote, t])

  // 1-Sekunden-Tick für die Ablauf-Anzeige.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [])

  // Polling auf Aktivierung.
  useEffect(() => {
    if (!quote) return
    let active = true
    const poll = async () => {
      const res = await refetchSubscription()
      const sub = res && 'data' in res ? res.data : undefined
      if (active && sub && sub.status === 'active') {
        onSuccess()
      }
    }
    const id = setInterval(() => { void poll() }, POLL_INTERVAL_MS)
    return () => { active = false; clearInterval(id) }
  }, [quote, refetchSubscription, onSuccess])

  const copy = useCallback(async (text: string, which: 'address' | 'amount') => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(which)
      setTimeout(() => setCopied(null), 1500)
    } catch {
      /* clipboard nicht verfügbar — kein harter Fehler */
    }
  }, [])

  const expiresMs = quote ? new Date(quote.expires_at).getTime() - now : 0
  const paymentUrl = quote ? buildHeliumPaymentUrl(quote) : ''

  return (
    <div data-testid="crypto-checkout">
      <div
        style={{
          background: 'var(--surface-elevated, var(--surface))',
          border: '1px solid var(--border)',
          borderRadius: 8,
          padding: '12px 16px',
          marginBottom: 16,
          fontSize: 14,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
          <span>{t('cryptoCheckout.summary.plan')}</span>
          <strong>{plan.name}</strong>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>{t('cryptoCheckout.summary.duration')}</span>
          <strong>{t('duration.month', { count: duration })}</strong>
        </div>
      </div>

      {error && (
        <p role="alert" style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 12 }}>
          {error}
        </p>
      )}

      {!quote && !error && (
        <p style={{ textAlign: 'center', color: 'var(--text-subtle)', padding: '24px 0' }} data-testid="crypto-loading">
          {t('cryptoCheckout.loading')}
        </p>
      )}

      {quote && (
        <>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
            <div style={{ background: '#fff', padding: 12, borderRadius: 8 }}>
              <QRCodeSVG value={paymentUrl} size={200} level="M" data-testid="crypto-qr" />
            </div>
          </div>

          <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--text-subtle)', marginBottom: 16 }}>
            {t('cryptoCheckout.scanInstructionPre')} <strong>{t('cryptoCheckout.scanInstructionExact')}</strong> {t('cryptoCheckout.scanInstructionPost')}
          </p>

          <div style={{ fontSize: 14, marginBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span>{t('cryptoCheckout.amountLabel')}</span>
              <button
                className="btn btn-ghost"
                style={{ padding: '0 6px', fontWeight: 600 }}
                onClick={() => { void copy(String(quote.amount), 'amount') }}
                data-testid="crypto-amount"
                title={t('cryptoCheckout.amountCopyTitle')}
              >
                {quote.amount} HNT {copied === 'amount' ? '✓' : '⧉'}
              </button>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span>≈ USD</span>
              <strong>${quote.amount_usd.toFixed(2)}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <span>{t('cryptoCheckout.walletLabel')}</span>
              <button
                className="btn btn-ghost"
                style={{ padding: '0 6px', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                onClick={() => { void copy(quote.wallet_address, 'address') }}
                data-testid="crypto-wallet"
                title={quote.wallet_address}
              >
                {quote.wallet_address.slice(0, 6)}…{quote.wallet_address.slice(-6)} {copied === 'address' ? '✓' : '⧉'}
              </button>
            </div>
          </div>

          <p style={{ textAlign: 'center', fontSize: 12, color: 'var(--text-subtle)', marginBottom: 4 }}>
            {t('cryptoCheckout.validFor')} <strong data-testid="crypto-expiry">{formatCountdown(expiresMs, t('cryptoCheckout.expired'))}</strong>
          </p>
          <p style={{ textAlign: 'center', fontSize: 12, color: 'var(--text-subtle)', marginBottom: 16 }} data-testid="crypto-waiting">
            {t('cryptoCheckout.waitingForPayment')}
          </p>
        </>
      )}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button className="btn btn-ghost" onClick={onClose}>{t('common:actions.close')}</button>
      </div>
    </div>
  )
}
